import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CircleNotch, WarningCircle } from "@phosphor-icons/react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // session_id can arrive in the URL fragment or (rarely) the query string.
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const match = (hash + "&" + search).match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    const run = async () => {
      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (e) {
        // Surface the real reason instead of silently bouncing to /login.
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Google sign-in failed.";
        console.error("Google sign-in failed:", e.response?.status, msg);
        setError(msg);
      }
    };
    run();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB] px-6" data-testid="auth-callback-error">
        <div className="w-full max-w-md rounded-sm border border-[#DC2626]/30 bg-white p-8 text-center shadow-sm">
          <WarningCircle size={36} weight="fill" className="mx-auto mb-4 text-[#DC2626]" />
          <h1 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">Sign-in didn’t complete</h1>
          <p className="mt-2 text-sm text-neutral-600" data-testid="auth-callback-error-message">{error}</p>
          <button
            data-testid="auth-callback-retry"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 w-full rounded-sm bg-[#0055FF] px-4 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC]"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB]" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <CircleNotch size={32} className="animate-spin text-[#0055FF]" />
        <p className="text-sm text-neutral-600">Signing you in…</p>
      </div>
    </div>
  );
}
